// ==============================================================================
// FILE: src/pages/DashboardPage.tsx (FEATURE: Smart Dashboard Navigation)
// PURPOSE: This file contains the interactive, operational dashboard.
//
// CHANGE 1: The `KpiCard` and `SlaCard` components are now interactive. An
//           `onClick` handler has been added to each relevant card.
// CHANGE 2: The `setInitialFiltersAndNavigate` function is retrieved from the
//           `useAppContext` hook.
// CHANGE 3: Clicking a card now calls this function with a specific, one-time
//           filter payload (e.g., for 'rejected' status or 'critical' SLA) and
//           navigates the user to the correct page (`ClaimHistory` or `ReviewQueue`).
// ==============================================================================
import React, { useEffect, useState, useCallback } from 'react';
import {
  Grid, Paper, Typography, Box, Card, CardContent, CircularProgress,
  CardActionArea, Button, TextField, useTheme
} from '@mui/material';
import {
  Warning as WarningIcon,
  Update as UpdateIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  RateReview as RateReviewIcon,
  Article as ArticleIcon,
  Event as EventIcon,
  CloudUpload as BulkIngestIcon
} from '@mui/icons-material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { useAppContext } from '../contexts/AppContext';
import { DashboardKpiData, DashboardSlaData, DashboardTrendData } from '../types';

// Card for Key Performance Indicators (KPIs)
const KpiCard = ({ title, value, icon, color, onClick }: { title: string, value: number, icon: React.ReactNode, color: string, onClick?: () => void }) => (
    <Card elevation={2} sx={{ height: '100%' }}>
        <CardActionArea onClick={onClick} disabled={!onClick} sx={{ height: '100%', p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{
                    p: 1.5,
                    borderRadius: '50%',
                    backgroundColor: `${color}.light`,
                    color: `${color}.dark`,
                    mr: 2,
                    display: 'flex',
                }}>
                    {icon}
                </Box>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>{value}</Typography>
                    <Typography color="text.secondary">{title}</Typography>
                </Box>
            </Box>
        </CardActionArea>
    </Card>
);

// Card for Service Level Agreement (SLA) status
const SlaCard = ({ title, value, description, icon, color, onClick }: { title: string, value: number, description: string, icon: React.ReactNode, color: string, onClick?: () => void }) => (
    <Card elevation={2} sx={{ height: '100%', borderTop: `4px solid ${useTheme().palette[color as 'error' | 'warning' | 'success'].main}` }}>
        <CardActionArea onClick={onClick} disabled={!onClick} sx={{ height: '100%', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {icon} {title}
                </Typography>
                <Typography variant="body2" color="text.secondary">{description}</Typography>
            </Box>
            <Typography variant="h3" sx={{ fontWeight: 700 }}>{value}</Typography>
        </CardActionArea>
    </Card>
);


export default function DashboardPage(): JSX.Element {
  const { apiService, setActivePage, showNotification, user, isSuperAdmin, setInitialFiltersAndNavigate } = useAppContext();
  const [kpiData, setKpiData] = useState<DashboardKpiData | null>(null);
  const [slaData, setSlaData] = useState<DashboardSlaData | null>(null);
  const [trendData, setTrendData] = useState<DashboardTrendData[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const today = new Date();
  const thirtyDaysAgo = new Date(new Date().setDate(today.getDate() - 30));

  const [filters, setFilters] = useState({
    start_date: thirtyDaysAgo.toISOString().split('T')[0],
    end_date: today.toISOString().split('T')[0],
  });

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleDatePreset = (preset: 'last_30_days' | 'this_month' | 'last_month') => {
      const endDate = new Date();
      let startDate = new Date();
      if (preset === 'last_30_days') {
          startDate.setDate(endDate.getDate() - 30);
      } else if (preset === 'this_month') {
          startDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
      } else if (preset === 'last_month') {
          startDate = new Date(endDate.getFullYear(), endDate.getMonth() - 1, 1);
          endDate.setDate(0); // Last day of previous month
      }
      setFilters({
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0]
      });
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const kpiPromise = apiService.getDashboardKpis(filters.start_date, filters.end_date);
      const slaPromise = apiService.getDashboardSla();
      const trendsPromise = apiService.getDashboardTrends();

      const [kpi, sla, trends] = await Promise.all([kpiPromise, slaPromise, trendsPromise]);

      setKpiData(kpi);
      setSlaData(sla);
      setTrendData(trends);
    } catch (err: any) {
      const errorMessage = 'Failed to load dashboard data. Please try again later.';
      setError(errorMessage);
      showNotification(err.message || errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  }, [apiService, showNotification, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Dashboard</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField name="start_date" label="Start Date" type="date" value={filters.start_date} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} size="small" />
            <TextField name="end_date" label="End Date" type="date" value={filters.end_date} onChange={handleFilterChange} InputLabelProps={{ shrink: true }} size="small" />
            <Button variant="outlined" size="small" onClick={() => handleDatePreset('last_30_days')}>Last 30 Days</Button>
            <Button variant="outlined" size="small" onClick={() => handleDatePreset('this_month')}>This Month</Button>
            <Button variant="outlined" size="small" onClick={() => handleDatePreset('last_month')}>Last Month</Button>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>Performance (This Period)</Typography>
        <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}><KpiCard title="Total Claims" value={kpiData?.total_claims || 0} icon={<ArticleIcon />} color="primary" onClick={() => setActivePage('Claim History')} /></Grid>
            <Grid item xs={12} sm={6} md={3}><KpiCard title="Processed" value={kpiData?.processed || 0} icon={<CheckCircleIcon />} color="success" onClick={() => setInitialFiltersAndNavigate({ status: ['completed', 'partially_approved'] }, 'Claim History')} /></Grid>
            <Grid item xs={12} sm={6} md={3}><KpiCard title="Rejected" value={kpiData?.rejected || 0} icon={<ErrorIcon />} color="error" onClick={() => setInitialFiltersAndNavigate({ status: ['rejected', 'failed_rejected'] }, 'Claim History')} /></Grid>
            <Grid item xs={12} sm={6} md={3}><KpiCard title="In Review" value={kpiData?.in_review || 0} icon={<PendingIcon />} color="warning" onClick={() => setInitialFiltersAndNavigate({ status: ['review_required'] }, 'Review Queue')} /></Grid>
        </Grid>
      </Box>
      
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>Action Required (Live Status)</Typography>
        <Grid container spacing={3}>
            <Grid item xs={12} md={4}><SlaCard title="Critical" value={slaData?.critical || 0} description="> 48 hrs waiting" icon={<WarningIcon sx={{ mr: 1 }} />} color="error" onClick={() => setInitialFiltersAndNavigate({ sla: 'critical' }, 'Review Queue')} /></Grid>
            <Grid item xs={12} md={4}><SlaCard title="Aging" value={slaData?.aging || 0} description="24-48 hrs waiting" icon={<UpdateIcon sx={{ mr: 1 }} />} color="warning" onClick={() => setInitialFiltersAndNavigate({ sla: 'aging' }, 'Review Queue')} /></Grid>
            <Grid item xs={12} md={4}><SlaCard title="New" value={slaData?.new || 0} description="< 24 hrs waiting" icon={<EventIcon sx={{ mr: 1 }} />} color="success" onClick={() => setInitialFiltersAndNavigate({ sla: 'new' }, 'Review Queue')} /></Grid>
        </Grid>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, height: 350, display: 'flex', flexDirection: 'column' }} elevation={2}>
            <Typography variant="h6" gutterBottom>Claim Review Trends</Typography>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="total_processed" name="Total Processed" stroke="#8884d8" strokeWidth={2} />
                <Line type="monotone" dataKey="manual_review" name="Needed Manual Review" stroke="#82ca9d" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 350 }} elevation={2}>
            <Typography variant="h6" gutterBottom>Quick Actions</Typography>
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
              <Button variant="contained" size="large" startIcon={<ArticleIcon />} onClick={() => setActivePage('Ingest')}>Ingest New Batch</Button>
              <Button variant="contained" size="large" startIcon={<RateReviewIcon />} onClick={() => setActivePage('Review Queue')}>Go to Review Queue</Button>
              {(user?.role === 'admin' || isSuperAdmin()) && (
                <Button variant="outlined" size="large" startIcon={<BulkIngestIcon />} onClick={() => setActivePage('Bulk Ingest')}>
                    Bulk Ingest
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
